<template>
  <v-container fluid class="pa-4">
    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="4000" location="top right">
      {{ snackbarText }}
      <template v-slot:actions>
        <v-btn icon="mdi-close" variant="text" @click="snackbar = false" />
      </template>
    </v-snackbar>

    <!-- Header -->
    <div class="d-flex align-center mb-4" style="gap: 12px;">
      <v-avatar color="grey-darken-3" size="40" rounded="lg">
        <v-icon color="white">mdi-account-cog</v-icon>
      </v-avatar>
      <div>
        <div class="text-h6 font-weight-bold">User Management</div>
        <div class="text-caption text-medium-emphasis">Manage system accounts and roles</div>
      </div>
      <v-spacer />
      <v-btn color="grey-darken-3" prepend-icon="mdi-account-plus" rounded="lg" @click="openAddDialog">
        Add User
      </v-btn>
    </div>

    <!-- Stats -->
    <v-row class="mb-4">
      <v-col cols="12" sm="4" v-for="s in roleStats" :key="s.label">
        <v-card variant="tonal" :color="s.color" class="pa-3 d-flex align-center" rounded="lg" style="gap: 12px;">
          <v-icon :color="s.color" size="32">{{ s.icon }}</v-icon>
          <div>
            <div class="text-h5 font-weight-bold">{{ s.count }}</div>
            <div class="text-caption text-medium-emphasis">{{ s.label }}</div>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Users Table -->
    <v-card variant="outlined" rounded="xl">
      <div class="pa-4 pb-2 d-flex align-center" style="gap: 12px;">
        <v-text-field
          v-model="search"
          prepend-inner-icon="mdi-magnify"
          label="Search users"
          variant="outlined"
          density="compact"
          hide-details
          clearable
          style="max-width: 300px;"
        />
        <v-select
          v-model="roleFilter"
          :items="['All', 'Admin', 'Operator']"
          label="Role"
          variant="outlined"
          density="compact"
          hide-details
          clearable
          style="max-width: 160px;"
        />
      </div>

      <v-data-table
        :headers="headers"
        :items="filteredUsers"
        item-key="id"
        :search="search"
        density="comfortable"
      >
        <template v-slot:[`item.role`]="{ item }">
          <v-chip
            :color="item.role === 'Admin' ? 'grey-darken-3' : 'blue'"
            size="small"
            variant="tonal"
            :prepend-icon="item.role === 'Admin' ? 'mdi-shield-crown' : 'mdi-account'"
          >
            {{ item.role }}
          </v-chip>
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <div class="d-flex align-center" style="gap: 4px;">
            <v-tooltip text="Edit user" location="top">
              <template v-slot:activator="{ props }">
                <v-btn v-bind="props" icon="mdi-pencil" size="small" color="blue" variant="tonal" @click="openEditDialog(item)" />
              </template>
            </v-tooltip>
            <v-tooltip :text="isSelf(item) ? 'Cannot delete own account' : 'Delete user'" location="top">
              <template v-slot:activator="{ props }">
                <v-btn v-bind="props" icon="mdi-delete" size="small" color="red" variant="tonal"
                  @click="deleteUser(item)"
                  :disabled="isSelf(item)"
                />
              </template>
            </v-tooltip>
          </div>
        </template>

        <template v-slot:no-data>
          <div class="text-center py-8 text-medium-emphasis">
            <v-icon size="48" class="mb-2">mdi-account-off-outline</v-icon>
            <div>No users found</div>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- Add / Edit Dialog -->
    <v-dialog v-model="dialog" max-width="480" persistent>
      <v-card rounded="xl" class="pa-2">
        <v-card-title class="pa-4 pb-2 d-flex align-center" style="gap: 8px;">
          <v-icon>{{ isEditing ? 'mdi-account-edit' : 'mdi-account-plus' }}</v-icon>
          {{ isEditing ? 'Edit User' : 'Add New User' }}
        </v-card-title>

        <v-card-text class="pt-2">
          <v-form ref="form" v-model="valid">
            <v-text-field
              v-model="formUser.name"
              label="Full Name"
              prepend-inner-icon="mdi-account-outline"
              variant="outlined"
              density="comfortable"
              :rules="[(v) => !!v || 'Name is required']"
              class="mb-2"
            />
            <v-text-field
              v-model="formUser.email"
              label="Email Address"
              type="email"
              prepend-inner-icon="mdi-email-outline"
              variant="outlined"
              density="comfortable"
              :rules="[(v) => !!v || 'Email is required', (v) => /.+@.+\..+/.test(v) || 'Invalid email']"
              class="mb-2"
            />
            <v-select
              v-model="formUser.role"
              :items="['Admin', 'Operator']"
              label="Role"
              prepend-inner-icon="mdi-shield-account-outline"
              variant="outlined"
              density="comfortable"
              :rules="[(v) => !!v || 'Role is required']"
              class="mb-2"
            />
            <v-text-field
              v-model="formUser.password"
              :label="isEditing ? 'New Password (leave blank to keep current)' : 'Password'"
              :type="showPwd ? 'text' : 'password'"
              prepend-inner-icon="mdi-lock-outline"
              :append-inner-icon="showPwd ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
              @click:append-inner="showPwd = !showPwd"
              variant="outlined"
              density="comfortable"
              :rules="isEditing ? [] : [(v) => !!v || 'Password is required', (v) => v.length >= 6 || 'Min 6 characters']"
            />
          </v-form>
        </v-card-text>

        <v-card-actions class="pa-4 pt-0">
          <v-spacer />
          <v-btn variant="text" @click="closeDialog">Cancel</v-btn>
          <v-btn color="grey-darken-3" variant="flat" rounded="lg" :loading="saving" @click="saveUser" prepend-icon="mdi-content-save">
            {{ isEditing ? 'Save Changes' : 'Create User' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import axios from "axios";

const emptyForm = () => ({ name: "", email: "", role: "", password: "" });

export default {
  data() {
    return {
      users: [],
      search: "",
      roleFilter: null,
      snackbar: false,
      snackbarText: "",
      snackbarColor: "success",
      dialog: false,
      valid: false,
      saving: false,
      isEditing: false,
      showPwd: false,
      formUser: emptyForm(),
      editingId: null,
      headers: [
        { title: "Name",    value: "name",    sortable: true  },
        { title: "Email",   value: "email",   sortable: true  },
        { title: "Role",    value: "role",    sortable: true  },
        { title: "Actions", value: "actions", sortable: false },
      ],
    };
  },

  computed: {
    filteredUsers() {
      if (!this.roleFilter || this.roleFilter === "All") return this.users;
      return this.users.filter((u) => u.role === this.roleFilter);
    },
    roleStats() {
      const admins    = this.users.filter((u) => u.role === "Admin").length;
      const operators = this.users.filter((u) => u.role === "Operator").length;
      return [
        { label: "Total Users",  count: this.users.length, icon: "mdi-account-group",  color: "blue"         },
        { label: "Admins",       count: admins,             icon: "mdi-shield-crown",   color: "grey-darken-3"},
        { label: "Operators",    count: operators,          icon: "mdi-account-wrench", color: "teal"         },
      ];
    },
    currentUserId() {
      try { return JSON.parse(localStorage.getItem("user"))?.id; } catch { return null; }
    },
  },

  mounted() {
    this.fetchUsers();
  },

  methods: {
    notify(text, color = "success") {
      this.snackbarText  = text;
      this.snackbarColor = color;
      this.snackbar      = true;
    },
    isSelf(user) {
      return String(user.id) === String(this.currentUserId);
    },
    async fetchUsers() {
      try {
        const { data } = await axios.get("/api/users");
        this.users = data;
      } catch (e) {
        this.notify("Failed to load users.", "red");
      }
    },
    openAddDialog() {
      this.isEditing  = false;
      this.editingId  = null;
      this.formUser   = emptyForm();
      this.showPwd    = false;
      this.dialog     = true;
      this.$nextTick(() => this.$refs.form?.resetValidation());
    },
    openEditDialog(user) {
      this.isEditing  = true;
      this.editingId  = user.id;
      this.formUser   = { name: user.name, email: user.email, role: user.role, password: "" };
      this.showPwd    = false;
      this.dialog     = true;
      this.$nextTick(() => this.$refs.form?.resetValidation());
    },
    closeDialog() {
      this.dialog   = false;
      this.formUser = emptyForm();
    },
    async saveUser() {
      const { valid } = await this.$refs.form.validate();
      if (!valid) return;
      this.saving = true;
      try {
        if (this.isEditing) {
          await axios.put(`/api/users/${this.editingId}`, this.formUser);
          this.notify("User updated successfully.");
        } else {
          await axios.post("/api/users", this.formUser);
          this.notify("User created successfully.");
        }
        this.closeDialog();
        await this.fetchUsers();
      } catch (e) {
        this.notify(e.response?.data?.error || "Operation failed.", "red");
      } finally {
        this.saving = false;
      }
    },
    async deleteUser(user) {
      if (!confirm(`Delete user "${user.name}"? This cannot be undone.`)) return;
      try {
        await axios.delete(`/api/users/${user.id}`);
        this.notify("User deleted.");
        await this.fetchUsers();
      } catch (e) {
        this.notify(e.response?.data?.error || "Delete failed.", "red");
      }
    },
  },
};
</script>
